/* eslint-disable no-console */
/* eslint-disable no-console */
import type { FetchResult } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  EmptyStation,
  type FormActionData,
  GenericField,
  MessageBar,
  ReadOnlyField,
  type StationMessage,
  TextButton,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import { Field } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { ensureError } from '../../../../common/errors/ensure-error';
import {
  RmqConnectionDocument,
  type RmqConnectionPayload,
  type RmqConnectionQuery,
  type RmqConnectionQueryVariables,
  SetupRmqUserDocument,
  type SetupRmqUserMutation,
  type SetupRmqUserMutationVariables,
} from '../../../../generated/hosting-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './RMQConnectionResetPassword.module.scss';

type RMQConnection = Omit<RmqConnectionPayload, '__typename'> | undefined;

export const RMQConnectionResetPassword: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [rmqConnectionData, setRMQConnectionData] =
    useState<RMQConnection>(undefined);
  const [defaultPassword, setDefaultPassword] = useState<string>();
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of hosting-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.HOSTING_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          const apolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
          );

          const setupRMQUser = async (): Promise<
            FetchResult<
              SetupRmqUserMutation,
              Record<string, unknown>,
              Record<string, unknown>
            >
          > => {
            return apolloClient.mutate<
              SetupRmqUserMutation,
              SetupRmqUserMutationVariables
            >({
              mutation: SetupRmqUserDocument,
              fetchPolicy: 'no-cache',
            });
          };

          const envMessagingSetupResult = await apolloClient.query<
            RmqConnectionQuery,
            RmqConnectionQueryVariables
          >({
            query: RmqConnectionDocument,
            fetchPolicy: 'no-cache',
          });

          if (envMessagingSetupResult.errors) {
            setError(envMessagingSetupResult.errors[0].message);
          } else {
            // If there is no rmqUser, it means that a user has not been setup for this environment.
            // Proceed to setup a new RMQ user.
            if (
              envMessagingSetupResult.data.rmqConnection !== null &&
              envMessagingSetupResult.data.rmqConnection?.rmqUser == null
            ) {
              const setupRmqUserResponse = await setupRMQUser();
              if (
                setupRmqUserResponse.data !== null &&
                setupRmqUserResponse.data?.setupRMQUser !== null
              ) {
                setRMQConnectionData(setupRmqUserResponse.data?.setupRMQUser);
                // Set a dummy default password, because we need to show the password for the newly generated connection
                setDefaultPassword('***');
              }
            } else if (
              envMessagingSetupResult.data.rmqConnection !== null &&
              envMessagingSetupResult.data.rmqConnection?.rmqPassword !== null
            ) {
              setRMQConnectionData(envMessagingSetupResult.data.rmqConnection);
              setDefaultPassword(
                envMessagingSetupResult.data.rmqConnection?.rmqPassword,
              );
            }
          }
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const { actions, newMessagingSetupData } = useGenerationActions(
    envId,
    defaultPassword !== undefined &&
      rmqConnectionData?.rmqPassword !== defaultPassword,
  );

  useEffect(() => {
    if (newMessagingSetupData) {
      setRMQConnectionData(newMessagingSetupData);
    }
  }, [newMessagingSetupData]);

  const Form: React.FC<{
    messagingSetupData: RMQConnection;
  }> = ({ messagingSetupData }) => {
    const onCopyClickedHandler = (): void => {
      try {
        // copy secret to clipboard
        navigator.clipboard.writeText(messagingSetupData?.rmqPassword ?? '');
      } catch (_error) {
        console.warn('Failed to copy to clipboard');
      }
    };

    const getConfigDownloadUrl = (): string => {
      const rmqConfig = `# RabbitMQ Connection
RABBITMQ_PROTOCOL=${messagingSetupData?.rmqProtocol}
RABBITMQ_HOST=${messagingSetupData?.rmqHost}
RABBITMQ_PORT=${messagingSetupData?.rmqPort}
RABBITMQ_MGMT_PROTOCOL=${messagingSetupData?.rmqMgmtProtocol}
RABBITMQ_MGMT_HOST=${messagingSetupData?.rmqMgmtHost}
RABBITMQ_MGMT_PORT=${messagingSetupData?.rmqMgmtPort}
RABBITMQ_VHOST=${messagingSetupData?.rmqVHost}
RABBITMQ_USER=${messagingSetupData?.rmqUser}
RABBITMQ_PASSWORD=${messagingSetupData?.rmqPassword}`;

      const blobConfig = new Blob([rmqConfig], {
        type: 'text/plain;charset=utf-8',
      });

      return URL.createObjectURL(blobConfig);
    };

    return (
      <>
        <div className={clsx(classes.password)}>
          <Field name="rmqPassword" label="Password" as={ReadOnlyField} />
          {defaultPassword !== undefined &&
            messagingSetupData?.rmqPassword !== defaultPassword && (
              <>
                <TextButton
                  className={classes.button}
                  text="Copy"
                  onButtonClicked={onCopyClickedHandler}
                />
              </>
            )}
        </div>
        {defaultPassword !== undefined &&
          messagingSetupData?.rmqPassword !== defaultPassword && (
            <GenericField
              name="allConfigurationSettings"
              label="All Configuration Settings"
            >
              <p>
                <a
                  href={getConfigDownloadUrl()}
                  target="_blank"
                  rel="noreferrer"
                  download="mosaic-rmq-config.env"
                >
                  Download
                </a>
              </p>
            </GenericField>
          )}
      </>
    );
  };

  const [message, setMessage] = useState<StationMessage>();
  useEffect(() => {
    if (
      defaultPassword !== undefined &&
      rmqConnectionData?.rmqPassword !== defaultPassword
    ) {
      setMessage({
        title:
          'WARNING: Please copy the password displayed below and store it in a secure location or download the connection details stored in a file and store it in a secure location. Once this station is closed, the password cannot be retrieved again and can only be replaced by generating a new configuration.',
        type: 'warning',
      });
    }
  }, [rmqConnectionData?.rmqPassword, defaultPassword]);

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="RabbitMQ Password">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details
          defaultTitle="RabbitMQ Password"
          actions={actions}
          initialData={{
            data: rmqConnectionData,
            loading,
            error,
          }}
          saveData={() => Promise.resolve()}
          stationMessage={message}
        >
          <Form messagingSetupData={rmqConnectionData} />
        </Details>
      )}
    </>
  );
};

const useGenerationActions = (
  envId: string,
  isNewPassword: boolean,
): {
  readonly actions: FormActionData[];
  readonly newMessagingSetupData: RMQConnection;
} => {
  const apolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
  );

  const generateEnvironmentMessagingPassword = async (): Promise<
    FetchResult<
      SetupRmqUserMutation,
      Record<string, unknown>,
      Record<string, unknown>
    >
  > => {
    return apolloClient.mutate<
      SetupRmqUserMutation,
      SetupRmqUserMutationVariables
    >({
      mutation: SetupRmqUserDocument,
      fetchPolicy: 'no-cache',
    });
  };

  // Generate a new password for the user
  const [newMessagingSetupData, setNewMessagingSetupData] =
    useState<RMQConnection>(undefined);

  const actions: FormActionData[] = [];
  if (!isNewPassword) {
    actions.push({
      label: 'Generate New Password',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Once you generate a new password, any existing consumers will need to be updated to use the new password.',
      },
      onActionSelected: async () => {
        const response = await generateEnvironmentMessagingPassword();
        if (response.data?.setupRMQUser !== null) {
          setNewMessagingSetupData(response.data?.setupRMQUser);
        }
      },
    });
  }

  return { actions, newMessagingSetupData } as const;
};

export const RMQConnectionResetPasswordCrumb: BreadcrumbResolver = () =>
  'Password';
